/**
 *  Checkbox wrapped in link to support SEO
 *
 * @module views/components/LinkCheckbox
 * @memberof -Common
 */
import './LinkCheckbox.scss';

import React, { forwardRef, useEffect, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import ResponseMessages from '@ulta/core/components/ResponseMessages/ResponseMessages';
import UseKeyPress from '@ulta/core/hooks/useKeyPress/UseKeyPress';
import { triggerDatacaptureEvent } from '@ulta/core/utils/datacapture/datacapture';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
import { isFunction } from '@ulta/core/utils/types/types';

import constants from '@ulta/utils/constants/constants';
/**
 * Represents a LinkCheckbox component
 *
 * @method
 * @param { LinkCheckboxProps} props - React properties passed from composition
 * @returns LinkCheckbox
 */
export const LinkCheckbox = forwardRef( ( props, ref )=>{
  const {
    checked: checkedState,
    href,
    name,
    id,
    onChange,
    dataCaptureData,
    children,
    error,
    disabled
  } = props;
  const [checked, setChecked] = useState( checkedState );

  useEffect( () => {
    setChecked( checkedState );
  }, [checkedState] );

  if( !isFunction( onChange ) ){
    return null;
  }

  // Handle SPACE key
  UseKeyPress( constants.SPACE_KEY, ref, ( event ) => {
    // We are checking if the checkbox is disabled to prevent
    // screen readers from checking the checkbox
    if( !disabled ){
      onChangeEv( { event, dataCaptureData, checked }, { onChange, setChecked } );
    }
    else {
      event.preventDefault();
    }

  } );

  return (
    <div className='CheckboxToggle_content'>
      <a
        href={ href }
        ref={ ref }
        role='checkbox'
        id={ id }
        name={ name }
        aria-checked={ checked }
        aria-disabled={ disabled }
        disabled={ disabled }
        onClick={ event => {
          if( !disabled ){
            onChangeEv( { event, dataCaptureData, checked }, { onChange, setChecked } );
          }
          event.preventDefault();
        } }
        className={
          classNames( 'CheckboxToggle', {
            'disabledLink':disabled
          } ) }
        { ...spreadProps( {
          'tabIndex': disabled ? '-1' : 0
        } ) }
      >
        <span className={
          classNames( 'CheckboxToggle__input', {
          } )
        }
        >
          <span className={
            classNames( 'CheckboxToggle__normalCheckBox', {
              'checked': checked,
              'CheckboxToggle__normalCheckBox CheckboxToggle__normalCheckBox--disabled': disabled
            } )
          }
          >
          </span>
        </span>
        <span>{ children }</span>
      </a>
      {
        error &&
          <ResponseMessages
            message={ error }
            messageType={ 'error' }
          />
      }
    </div>

  );
} );


/**
  * @method onChangeEv
  * @summary to handle click event of checkbox
  * @param {object} methods - passing toggleFilterGroupListItem function
  * @param {object} data - passing filterItem data
  * @returns {undefined}
  */
export const onChangeEv = ( data, methods ) => {
  const { event, dataCaptureData, checked } = handleEmptyObjects( data );
  const { onChange, setChecked } = handleEmptyObjects( methods );
  event.preventDefault();
  setChecked( !checked );
  onChange( { checked, event } );

  if( dataCaptureData ){
    triggerDatacaptureEvent( dataCaptureData );
  }
};

/**
 * Property type definitions
 * @typedef LinkCheckboxProps
 * @type {object}
 * @property {boolean} checked - Sets the state of toggle button
 * @property {string} href - anchor link for seo
 * @property {string} name - Sets the name attribute which is required field
 * @property {string} id - Sets id attribute for toggle button
 * @property {method} onChange - to change the checkbox value on click
 * @property {object} dataCaptureData - data to trigger datacapture event
 * @property {boolean} disabled - Sets the user behavior of toggle button
 * @property {string} error - Sets the exclamationcircle svg when there is error
 */

export const propTypes =  {
  checked: PropTypes.bool,
  href:PropTypes.string,
  name:PropTypes.string,
  id:PropTypes.string,
  onChange:PropTypes.func,
  dataCaptureData:PropTypes.object,
  disabled:PropTypes.bool,
  error:PropTypes.string

};


LinkCheckbox.propTypes = propTypes;
LinkCheckbox.displayName = 'LinkCheckbox';

export default LinkCheckbox;
